import React from 'react';

export interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// Neural Network Icon for AI-Powered Analysis
const NeuralNetworkIcon = (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Input Layer */}
    <circle cx="8" cy="14" r="3" fill="white" opacity="0.9"/>
    <circle cx="8" cy="24" r="3" fill="white" opacity="0.9"/>
    <circle cx="8" cy="34" r="3" fill="white" opacity="0.9"/>
    
    {/* Hidden Layer */}
    <circle cx="24" cy="10" r="3.5" fill="white"/>
    <circle cx="24" cy="20" r="3.5" fill="white"/>
    <circle cx="24" cy="28" r="3.5" fill="white"/>
    <circle cx="24" cy="38" r="3.5" fill="white"/>
    
    {/* Output Layer */}
    <circle cx="40" cy="18" r="3" fill="white" opacity="0.9"/>
    <circle cx="40" cy="30" r="3" fill="white" opacity="0.9"/>
    
    {/* Connection Lines - Input to Hidden */}
    <line x1="11" y1="14" x2="21" y2="10" stroke="white" strokeWidth="1" opacity="0.3"/>
    <line x1="11" y1="14" x2="21" y2="20" stroke="white" strokeWidth="1" opacity="0.3"/>
    <line x1="11" y1="24" x2="21" y2="20" stroke="white" strokeWidth="1" opacity="0.4"/>
    <line x1="11" y1="24" x2="21" y2="28" stroke="white" strokeWidth="1" opacity="0.4"/>
    <line x1="11" y1="34" x2="21" y2="28" stroke="white" strokeWidth="1" opacity="0.3"/>
    <line x1="11" y1="34" x2="21" y2="38" stroke="white" strokeWidth="1" opacity="0.3"/>
    
    {/* Connection Lines - Hidden to Output */}
    <line x1="27" y1="10" x2="37" y2="18" stroke="white" strokeWidth="1" opacity="0.3"/>
    <line x1="27" y1="20" x2="37" y2="18" stroke="white" strokeWidth="1" opacity="0.4"/>
    <line x1="27" y1="20" x2="37" y2="30" stroke="white" strokeWidth="1" opacity="0.4"/>
    <line x1="27" y1="28" x2="37" y2="30" stroke="white" strokeWidth="1" opacity="0.4"/>
    <line x1="27" y1="38" x2="37" y2="30" stroke="white" strokeWidth="1" opacity="0.3"/>
  </svg>
);

// Official MCP Icon for Agent Enabling
const MCPIcon = (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="white" fillRule="evenodd" xmlns="http://www.w3.org/2000/svg">
    <title>ModelContextProtocol</title>
    <path d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z"></path>
    <path d="M14.485 4.703a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a4.115 4.115 0 000 5.9 4.314 4.314 0 006.016 0l7.12-6.982a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a2.588 2.588 0 01-3.61 0 2.47 2.47 0 010-3.54l7.12-6.982z"></path>
  </svg>
);

// Forecasting Chart Icon
const ForecastingIcon = (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Axes */}
    <line x1="6" y1="40" x2="44" y2="40" stroke="white" strokeWidth="1.5" opacity="0.4"/>
    <line x1="6" y1="6" x2="6" y2="40" stroke="white" strokeWidth="1.5" opacity="0.4"/>
    
    {/* One continuous smooth trend line */}
    <path d="M 8 34 Q 14 29, 20 23 T 32 12 T 42 5" 
          stroke="white" 
          strokeWidth="3" 
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"/>
    
    {/* Data points along the line */}
    <circle cx="8" cy="34" r="2.5" fill="white"/>
    <circle cx="20" cy="23" r="2.5" fill="white"/>
    <circle cx="32" cy="12" r="2.5" fill="white"/>
    
    {/* Predictive arrow at the end */}
    <path d="M 42 5 L 45 2" 
          stroke="white" 
          strokeWidth="2.5"
          strokeLinecap="round"/>
    <path d="M 45 2 L 43 3.5 M 45 2 L 43.5 0.5" 
          stroke="white" 
          strokeWidth="2"
          strokeLinecap="round"/>
  </svg>
);

export const enterpriseFeatures: Feature[] = [
  {
    icon: NeuralNetworkIcon,
    title: 'AI-Powered Analysis',
    description: 'Advanced machine learning algorithms automatically identify patterns and anomalies in complex geospatial datasets.'
  },
  {
    icon: MCPIcon,
    title: 'Agent Enabling',
    description: 'Khyros uses MCP (Model Context Protocol) to seamlessly connect LLMs and AI agents with geospatial data, enabling them to perform complex analysis.'
  },
  {
    icon: ForecastingIcon,
    title: 'Predictive Forecasting',
    description: 'Leverage historical patterns to predict future events and identify potential threats before they emerge.'
  }
];

